import { isBrowser } from '@nzyme/dom-utils/isBrowser.js';
import { defineService } from '@nzyme/ioc/Service.js';
import { Logger } from '@nzyme/logging/Logger.js';
import { createEventEmitter } from '@nzyme/utils/createEventEmitter.js';
import { createSingleRunner } from '@nzyme/utils/createSingleRunner.js';
import { computed, reactive, ref, watch } from 'vue';
import { ActorClient } from '@sigma/actors-client/ActorClient.js';
import { API_APP, getAppUrl } from '@sigma/env/apps.js';
import { AuthStore } from '../auth/AuthStore.js';
import { BrowserTabService } from './BrowserTabService.js';
/**
 * Service for managing ActorClient WebSocket connection lifecycle.
 * Handles connection state, reconnection on tab visibility changes,
 * and authentication via query parameters.
 */
export const ActorConnectionService = defineService({
    name: 'ActorConnectionService',
    deps: {
        actorClient: ActorClient,
        authStore: AuthStore,
        browserTabService: BrowserTabService,
        logger: Logger,
    },
    setup({ actorClient, authStore, browserTabService, logger }) {
        const eventConnected = createEventEmitter();
        const eventDisconnected = createEventEmitter();
        // Whether connection is requested (ie. we want to be connected)
        const connectionRequested = ref(false);
        // Computed connection status based on browser tab and ActorClient state
        const connected = computed(() => {
            switch (browserTabService.status) {
                case 'offline':
                    return false;
                case 'online':
                    return actorClient.status === 'CONNECTED' ? true : undefined;
                default:
                    return undefined;
            }
        });
        const connectRunner = createSingleRunner({ handler: doConnect });
        // Watch browser tab status for background/foreground switching
        watch(() => browserTabService.status, newStatus => {
            if (!connectionRequested.value) {
                return;
            }
            switch (newStatus) {
                case 'offline':
                    void disconnect();
                    break;
                case 'online':
                case 'resuming':
                    void connectRunner.execute();
                    break;
                default:
                    break;
            }
        });
        watch(() => authStore.user?.id, async (newId) => {
            if (!newId) {
                await disconnect();
            }
        });
        // Forward ActorClient events
        actorClient.connected.on(() => {
            eventConnected.emit();
        });
        actorClient.disconnected.on(() => {
            eventDisconnected.emit();
            // Auto-reconnect if connection was requested
            if (connectionRequested.value && browserTabService.status === 'online') {
                void connectRunner.execute();
            }
        });
        actorClient.beforeRequest.on(connect);
        return reactive({
            connected,
            status: computed(() => actorClient.status),
            connect,
            disconnect,
            events: {
                connected: eventConnected.event,
                disconnected: eventDisconnected.event,
            },
        });
        /**
         * Request connection to the actor gateway.
         */
        async function connect() {
            if (!isBrowser()) {
                return;
            }
            if (actorClient.status === 'CONNECTED') {
                return;
            }
            connectionRequested.value = true;
            if (browserTabService.status !== 'online') {
                return;
            }
            await connectRunner.execute();
        }
        /**
         * Perform the actual connection.
         */
        async function doConnect() {
            if (actorClient.status === 'CONNECTED') {
                return;
            }
            // Ensure auth is checked
            await authStore.checkAuth();
            const token = authStore.auth?.idToken;
            if (!token) {
                return;
            }
            const baseUrl = `${getAppUrl(API_APP)}/ws`;
            logger.debug('Connecting to actor gateway', { baseUrl });
            await actorClient.connect(baseUrl, {
                query: { token },
            });
        }
        /**
         * Request disconnection from the actor gateway.
         */
        async function disconnect() {
            connectionRequested.value = false;
            if (!isBrowser()) {
                return;
            }
            logger.debug('Disconnecting from actor gateway');
            await actorClient.close();
        }
    },
});
//# sourceMappingURL=ActorConnectionService.js.map